/**
 * Scatter chart view component using Recharts.
 */

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ZAxis,
} from 'recharts';
import type { ChartConfig, ChartReferenceLine, TransformedChartData } from '../../types/chartTypes';
import { DEFAULT_CHART_COLORS } from '../../types/chartTypes';
import { abbreviateAxisTick, CARTESIAN_MARGIN, formatTooltipValue } from '../../utils/axisFormat';
import { renderChartReferenceLines } from './chartReferenceLines';

type ScatterChartViewProps = {
  data: TransformedChartData;
  config: ChartConfig;
  height?: number | `${number}%`;
  /** Recharts default is true; dashboard tiles animate only on first mount. */
  isAnimationActive?: boolean;
  /** Target/goal lines (dashboard tiles). */
  referenceLines?: ChartReferenceLine[];
  /** Raw mark-click passthrough (S11) — ChartRenderer normalizes/resolves. */
  onMark?: (...args: unknown[]) => void;
  /** ResponsiveContainer resize debounce (ms) — dashboard tiles pass ~150. */
  resizeDebounceMs?: number;
};

export function ScatterChartView({
  data,
  config,
  height = 400,
  isAnimationActive,
  referenceLines,
  onMark,
  resizeDebounceMs,
}: ScatterChartViewProps) {
  const colors = config.colors || DEFAULT_CHART_COLORS;

  if (data.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No data to display
      </div>
    );
  }

  // Group data by series
  const seriesData = new Map<string, Array<{ x: number; y: number }>>();
  for (const point of data.data) {
    const seriesKey = (point.series as string) || 'data';
    if (!seriesData.has(seriesKey)) {
      seriesData.set(seriesKey, []);
    }
    seriesData.get(seriesKey)!.push({
      x: point.x as number,
      y: point.y as number,
    });
  }

  const xLabel = config.xAxis.label || config.xAxis.column;
  const yLabel = config.yAxis[0]?.label || config.yAxis[0]?.column || 'y';

  return (
    <ResponsiveContainer width="100%" height={height} debounce={resizeDebounceMs}>
      <ScatterChart margin={CARTESIAN_MARGIN}>
        {config.showGrid && <CartesianGrid strokeDasharray="3 3" />}
        {/* The X axis keeps raw ticks: a numeric X often carries years/IDs,
            which must not read as "2k". Abbreviation is a Y-axis (measure)
            affordance. */}
        <XAxis type="number" dataKey="x" name={xLabel} tick={{ fontSize: 12 }} />
        {/* preserveStartEnd keeps the 0 baseline label when ticks are culled. */}
        <YAxis
          type="number"
          dataKey="y"
          name={yLabel}
          tick={{ fontSize: 12 }}
          tickFormatter={abbreviateAxisTick}
          interval="preserveStartEnd"
        />
        <ZAxis range={[50, 50]} />
        {renderChartReferenceLines(referenceLines)}
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          contentStyle={{
            backgroundColor: 'var(--background)',
            border: '1px solid var(--border)',
            borderRadius: '0.375rem',
          }}
          formatter={(value, name) => [formatTooltipValue(value), name === 'x' ? xLabel : yLabel]}
        />
        {config.showLegend && <Legend verticalAlign="top" height={36} />}
        {Array.from(seriesData.entries()).map(
          ([seriesKey, points], index) => (
            <Scatter
              key={seriesKey}
              isAnimationActive={isAnimationActive}
              name={seriesKey}
              data={points}
              fill={colors[index % colors.length]}
              onClick={onMark}
              cursor={onMark ? 'pointer' : undefined}
            />
          )
        )}
      </ScatterChart>
    </ResponsiveContainer>
  );
}
